import { Button } from '@ordr-as/umami-react/button';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { formatWithCurrency } from '../../utils/format';
import { useCallback, useEffect, useState } from 'react';
import { calculateTipAmount, calculateTotal } from './TipsCalculator';
import Spinner from '../Spinner';

interface TextRotatorProps {
  texts: string[]; // Array of strings
}

const TextRotator: React.FC<TextRotatorProps> = ({ texts }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  useEffect(() => {
    // Only set the interval if we haven't reached the end of the array
    if (currentIndex < texts.length - 1) {
      const intervalId = setInterval(() => {
        setCurrentIndex((prevIndex) => prevIndex + 1);
      }, 2000); // Change text every 2 seconds

      // Cleanup the interval on component unmount or when reaching the end
      return () => clearInterval(intervalId);
    }
  }, [currentIndex, texts.length]); // Dependency array includes currentIndex and texts.length

  return <>{texts[currentIndex]}</>;
};

interface Props {
  cancelTip: () => void;
  confirmTip: (tipAmount: number) => void;
  totalPrice: number;
  currency: string;
  tipConfig?: TipConfig;
}

interface TipConfig {
  tipOptions: number[];
  tipDisplayMode: 'PERCENTAGE' | 'AMOUNT' | 'PERCENTAGE_AMOUNT';
  type: 'PERCENTAGE' | 'AMOUNT';
}

const defaultTipConfig: TipConfig = {
  tipDisplayMode: 'PERCENTAGE_AMOUNT',
  tipOptions: [5, 10, 15, 20],
  type: 'PERCENTAGE',
};

const TipsDialog = (props: Props) => {
  const { totalPrice, tipConfig = defaultTipConfig, currency } = props;
  const { tipOptions, tipDisplayMode } = tipConfig;
  const [totalAmount, setTotalAmount] = useState(totalPrice);
  const [tipButtonsDisabled, setTipButtonsDisabled] = useState(false);

  const paymentSteps: string[] = [
    'Initiating payment...',
    'Verifying...',
    'Processing...',
    'Securing...',
    'Finalizing...',
    'Redirecting...',
  ];

  const updateTotalAmount = (tipPercentage: number) => {
    const tipAmount = calculateTipAmount(totalPrice, tipPercentage);
    const newTotalPrice = calculateTotal(totalPrice, tipPercentage);
    setTotalAmount(newTotalPrice);
    setTipButtonsDisabled(true);

    props.confirmTip(tipAmount);

    setTimeout(() => {
      setTotalAmount(0);
    }, 3000);
  };

  const getTipText = useCallback(
    (tip: number) => {
      switch (tipDisplayMode) {
        case 'PERCENTAGE': {
          return `${tip}%`;
        }
        case 'AMOUNT': {
          return `${formatWithCurrency(tip, currency)}`;
        }
        case 'PERCENTAGE_AMOUNT': {
          return `${tip}% | ${formatWithCurrency(calculateTipAmount(totalPrice, tip), currency)}`;
        }
      }
    },
    [currency, tipDisplayMode, totalPrice]
  );

  const skipTip = () => {
    updateTotalAmount(0);
  };

  if (tipButtonsDisabled) {
    return (
      <div className="fixed inset-0 z-40 flex bg-brand-secondary">
        <div className="flex flex-col w-full">
          <div className="flex flex-col justify-center items-center flex-grow">
            <div>
              <Spinner className="size-8" />
            </div>
            <div className="mt-5">
              <h2 className="text-brand-white">
                <TextRotator texts={paymentSteps} />
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (totalAmount === 0) {
    return null;
  }

  return (
    <Dialog
      className="w-full mx-4 tablet:w-1/2 tablet:mx-0"
      onClose={() => {}}
      hideHeader
      title=""
      secondaryActionLabel="No tip"
      onSecondaryAction={skipTip}
      isDisabledAction={tipButtonsDisabled}
      isDisabledSecondaryAction={tipButtonsDisabled}
      size="fullscreen"
    >
      <div className="flex flex-col justify-between size-full">
        <div className="size-full flex flex-col justify-end mb-8">
          <h2 className="text-center text-6xl">{formatWithCurrency(totalAmount, currency)}</h2>
        </div>
        <div data-up="tip-container" className="tip-container flex flex-col justify-center text-center">
          {tipOptions.map((tip) => (
            <Button
              aria-label="tip button ${tip}"
              size="medium"
              fullWidth
              kind="primary"
              className="mb-3 p-4 font-bold"
              isDisabled={tipButtonsDisabled}
              key={tip}
              onClick={() => updateTotalAmount(tip)}
            >
              {getTipText(tip)}
            </Button>
          ))}
        </div>
      </div>
    </Dialog>
  );
};

export default TipsDialog;
